const year = new Date().getFullYear();

export type ArticleEntry = {
  slug: string;
  verticalPath?: string;
  title: string;
  group: string;
  parent?: string;
};

export type MonetizingLink = {
  url: string;
  action: string;
  text: string;
};

export type Collection = {
  sectionNavTitle: string;
  sectionType: string;
  sectionShortTitle: string;
  monetizingLink: MonetizingLink;
  articles: Array<ArticleEntry>;
};

type ResourceArticles = {
  [s: string]: Collection;
};

const resourceArticles: ResourceArticles = {
  'ira-101': {
    sectionNavTitle: 'Individual Retirement Accounts (IRA) 101',
    sectionType: 'Guides',
    sectionShortTitle: 'IRA 101',
    monetizingLink: {
      url:
        'https://www.nerdwallet.com/blog/investing/the-best-ira-account-providers/',
      action: 'Choose a provider',
      text: `Best IRAs in ${year}`,
    },
    articles: [
      {
        slug: 'learn-about-ira-accounts',
        verticalPath: 'investing',
        title: 'What is an IRA?',
        group: 'ira-101',
      },
      {
        slug: 'what-is-a-roth-ira',
        verticalPath: 'investing',
        title: 'What is a Roth IRA?',
        group: 'ira-101',
      },
      {
        slug: 'roth-or-traditional-ira-account',
        verticalPath: 'investing',
        title: 'Roth vs. traditional IRA',
        group: 'ira-101',
      },
      {
        slug: 'ira-contribution-limits',
        verticalPath: 'investing',
        title: 'IRA contribution and income limits',
        group: 'ira-101',
      },
      {
        slug: 'ira-vs-401k-retirement-accounts',
        verticalPath: 'investing',
        title: 'IRA vs. 401(k)',
        group: 'ira-101',
      },
      {
        slug: 'how-to-rollover-401k-roth-traditional-ira',
        verticalPath: 'investing',
        title: 'What is a rollover IRA?',
        group: 'ira-101',
      },
      {
        slug: 'how-and-where-to-open-an-ira',
        verticalPath: 'investing',
        title: 'How to open an IRA',
        group: 'ira-101',
      },
      {
        slug: 'how-to-invest-ira',
        verticalPath: 'investing',
        title: 'Choosing IRA investments',
        group: 'ira-101',
      },
    ],
  },
  'fha-loans-101': {
    sectionNavTitle: 'FHA Loans 101',
    sectionType: 'Guides',
    sectionShortTitle: 'FHA Loans 101',
    monetizingLink: {
      url:
        'https://www.nerdwallet.com/blog/mortgages/best-lenders-for-fha-loans/',
      action: 'Choose a provider',
      text: 'Best FHA Lenders',
    },
    articles: [
      {
        slug: 'fha-loan',
        verticalPath: 'mortgages',
        title: "What's an FHA loan?",
        group: 'fha-loans-101',
      },
      {
        slug: 'fha-loan-vs-conventional-mortgage',
        verticalPath: 'mortgages',
        title: 'FHA vs. conventional loans',
        group: 'fha-loans-101',
      },
      {
        slug: 'fha-loan-requirements',
        verticalPath: 'mortgages',
        title: 'FHA loan eligibility and requirements',
        group: 'fha-loans-101',
      },
      {
        slug: 'fha-loans-low-down-payment',
        verticalPath: 'mortgages',
        title: 'FHA down payment requirements',
        group: 'fha-loans-101',
        parent: 'fha-loan-requirements',
      },
      {
        slug: 'fha-mortgage-insurance',
        verticalPath: 'mortgages',
        title: 'FHA mortgage insurance',
        group: 'fha-loans-101',
        parent: 'fha-loan-requirements',
      },
      {
        slug: 'fha-closing-costs',
        verticalPath: 'mortgages',
        title: 'FHA closing costs',
        group: 'fha-loans-101',
      },
      {
        slug: 'fha-mortgage-calculator',
        verticalPath: 'mortgages',
        title: 'Calculate your FHA mortgage',
        group: 'fha-loans-101',
      },
      {
        slug: 'https://www.nerdwallet.com/mortgages/mortgage-rates/fha',
        title: 'Compare FHA mortgage rates',
        group: 'fha-loans-101',
      },
    ],
  },
  'va-loans-101': {
    sectionNavTitle: 'VA Loans 101',
    sectionType: 'Guides',
    sectionShortTitle: 'VA Loans 101',
    monetizingLink: {
      url:
        'https://www.nerdwallet.com/blog/mortgages/best-va-mortgage-lenders/',
      action: 'Choose a provider',
      text: `Best VA Lenders in ${year}`,
    },
    articles: [
      {
        slug: 'va-home-loan',
        verticalPath: 'mortgages',
        title: "What's a VA loan?",
        group: 'va-loans-101',
      },
      {
        slug: 'va-loans-vs-conventional-loans',
        verticalPath: 'mortgages',
        title: 'VA vs. conventional loans',
        group: 'va-loans-101',
      },
      {
        slug: 'va-loan-eligibility-requirements',
        verticalPath: 'mortgages',
        title: 'VA loan eligibility and requirements',
        group: 'va-loans-101',
      },
      {
        slug: 'va-loan-funding-fee-explained',
        verticalPath: 'mortgages',
        title: 'VA loan funding fee',
        group: 'va-loans-101',
        parent: 'va-loan-eligibility-requirements',
      },
      {
        slug: 'va-loan-limits',
        verticalPath: 'mortgages',
        title: 'VA loan limits',
        group: 'va-loans-101',
        parent: 'va-loan-eligibility-requirements',
      },
      {
        slug: 'how-to-refinance-a-va-loan',
        verticalPath: 'mortgages',
        title: 'VA refinancing requirements',
        group: 'va-loans-101',
        parent: 'va-loan-eligibility-requirements',
      },
      {
        slug: 'va-mortgage-calculator',
        verticalPath: 'mortgages',
        title: 'Calculate your VA payments',
        group: 'va-loans-101',
      },
      {
        slug: 'https://www.nerdwallet.com/mortgages/mortgage-rates/va',
        title: 'Compare VA mortgage rates',
        group: 'va-loans-101',
      },
    ],
  },
  'first-time-home-buyer-guide': {
    sectionNavTitle: 'First-time home buyer guide',
    sectionType: 'Guides',
    sectionShortTitle: 'First Time Home Buyer',
    monetizingLink: {
      url:
        'https://www.nerdwallet.com/best/mortgages/mortgage-lenders-first-time-home-buyers',
      action: 'Choose a provider',
      text: 'Best Mortgage Lenders',
    },
    articles: [
      {
        slug: 'tips-for-first-time-home-buyers',
        verticalPath: 'mortgages',
        title: 'Tips to get finances ready',
        group: 'first-time-home-buyer-guide',
      },
      {
        slug:
          'https://www.nerdwallet.com/mortgages/how-much-house-can-i-afford',
        title: 'How much house can you afford?',
        group: 'first-time-home-buyer-guide',
        parent: 'tips-for-first-time-home-buyers',
      },
      {
        slug: 'payment-buy-home',
        verticalPath: 'mortgages',
        title: 'How much down payment?',
        group: 'first-time-home-buyer-guide',
        parent: 'tips-for-first-time-home-buyers',
      },
      {
        slug: 'https://www.nerdwallet.com/mortgages/mortgage-calculator',
        title: 'Calculate your mortgage payment',
        group: 'first-time-home-buyer-guide',
        parent: 'tips-for-first-time-home-buyers',
      },
      {
        slug: 'credit-score-affects-mortgage-rate',
        verticalPath: 'mortgages',
        title: 'Get your credit score in shape',
        group: 'first-time-home-buyer-guide',
        parent: 'tips-for-first-time-home-buyers',
      },
      {
        slug: 'documents-needed-for-a-mortgage-preapproval',
        verticalPath: 'mortgages',
        title: 'Collecting preapproval documents',
        group: 'first-time-home-buyer-guide',
        parent: 'tips-for-first-time-home-buyers',
      },
      {
        slug: 'programs-help-first-time-homebuyers',
        verticalPath: 'mortgages',
        title: 'Find the best mortgage type for you',
        group: 'first-time-home-buyer-guide',
      },
      {
        slug: 'https://www.nerdwallet.com/mortgages/mortgage-rates',
        title: 'Compare mortgage rates',
        group: 'first-time-home-buyer-guide',
      },
      {
        slug: 'closing-costs-calculator',
        verticalPath: 'mortgages',
        title: 'Calculate your closing costs',
        group: 'first-time-home-buyer-guide',
      },
    ],
  },
  'retirement-planning-guide': {
    sectionNavTitle: "NerdWallet's Guide to Retirement Planning",
    sectionType: 'Guides',
    sectionShortTitle: "NerdWallet's Guide to Retirement Planning",
    monetizingLink: {
      url:
        'https://www.nerdwallet.com/blog/investing/the-best-ira-account-providers/',
      action: 'No retirement account?',
      text: 'See the best options',
    },
    articles: [
      {
        slug: 'retirement-planning-an-introduction',
        verticalPath: 'investing',
        title: 'Retirement Planning: An Introduction',
        group: 'retirement-planning-guide',
      },
      {
        slug: 'financial-goals',
        verticalPath: 'investing',
        title: 'Chapter 1: Financial Goals: Where to Begin',
        group: 'retirement-planning-guide',
        parent: 'retirement-planning-an-introduction',
      },
      {
        slug: 'how-to-save-for-retirement',
        verticalPath: 'investing',
        title: 'Chapter 2: How to Save for Retirement',
        group: 'retirement-planning-guide',
        parent: 'retirement-planning-an-introduction',
      },
      {
        slug: 'investing-101',
        verticalPath: 'investing',
        title: 'Chapter 3: Investing 101 ',
        group: 'retirement-planning-guide',
        parent: 'retirement-planning-an-introduction',
      },
      {
        slug: 'what-to-invest-in',
        verticalPath: 'investing',
        title: 'Chapter 4: What to Invest In',
        group: 'retirement-planning-guide',
        parent: 'retirement-planning-an-introduction',
      },
      {
        slug: 'how-to-build-wealth',
        verticalPath: 'investing',
        title: 'Chapter 5: How to Build Wealth',
        group: 'retirement-planning-guide',
        parent: 'retirement-planning-an-introduction',
      },
    ],
  },
  'pay-off-student-loans-faster': {
    sectionNavTitle: 'Pay Off Student Loans Faster',
    sectionType: 'Guides',
    sectionShortTitle: 'Pay Off Student Loans Faster',
    monetizingLink: {
      url:
        'https://www.nerdwallet.com/best/loans/student-loans/refinance-lenders-paying-off-student-loans-fast',
      action: null,
      text: 'The best refinancing lenders for fast student loan repayment',
    },
    articles: [
      {
        slug: 'pay-off-student-loans-fast',
        verticalPath: 'loans/student-loans',
        title: 'Tips for quick payoff',
        group: 'pay-off-student-loans-faster',
      },
      {
        slug: 'biweekly-student-loan-payments',
        verticalPath: 'loans/student-loans',
        title: 'Make biweekly payments',
        group: 'pay-off-student-loans-faster',
        parent: 'pay-off-student-loans-fast',
      },
      {
        slug: 'how-to-lower-student-loan-interest-rate',
        verticalPath: 'loans/student-loans',
        title: 'Lower your interest rate',
        group: 'pay-off-student-loans-faster',
        parent: 'pay-off-student-loans-fast',
      },
      {
        slug: 'student-loan-interest-capitalization',
        verticalPath: 'loans/student-loans',
        title: 'Pay off capitalized interest',
        group: 'pay-off-student-loans-faster',
        parent: 'pay-off-student-loans-fast',
      },
      {
        slug: 'employer-student-loan-repayment',
        verticalPath: 'loans/student-loans',
        title: 'Get help from your employer',
        group: 'pay-off-student-loans-faster',
        parent: 'pay-off-student-loans-fast',
      },
      {
        slug: 'student-loan-grace-period',
        verticalPath: 'loans/student-loans',
        title: 'Make use of the grace period',
        group: 'pay-off-student-loans-faster',
        parent: 'pay-off-student-loans-fast',
      },
      {
        slug: 'pay-off-student-loans-early',
        verticalPath: 'loans/student-loans',
        title: 'Should you pay off loans early?',
        group: 'pay-off-student-loans-faster',
      },
      {
        slug: 'save-invest-pay-off-student-loans',
        verticalPath: 'loans/student-loans',
        title: 'How to save and invest, too',
        group: 'pay-off-student-loans-faster',
      },
      {
        slug: 'student-loans-extra-payments',
        verticalPath: 'loans/student-loans',
        title: 'Student loan payoff calculator',
        group: 'pay-off-student-loans-faster',
      },
    ],
  },
};

export default resourceArticles;
